import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Loader2, Printer, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const CONFIG = {
  assets: {
    title: "Asset Register",
    fetch: () => api.listAssets(),
    columns: [
      { k: "asset_code", label: "Code", mono: true },
      { k: "name", label: "Name" },
      { k: "category", label: "Category" },
      { k: "makemodel", label: "Make / Model", get: (r) => [r.make, r.model].filter(Boolean).join(" ") },
      { k: "location", label: "Location" },
      { k: "status", label: "Status", badge: true },
      { k: "pat_status", label: "PAT", badge: true },
    ],
  },
  faults: {
    title: "Fault Register",
    fetch: () => api.listFaults({ status: "all" }),
    columns: [
      { k: "title", label: "Fault" },
      { k: "priority", label: "Priority", badge: true },
      { k: "status", label: "Status", badge: true },
      { k: "where", label: "Asset / Area", get: (r) => r.asset_name ? `${r.asset_code} ${r.asset_name}` : (r.area || "General") },
      { k: "reported_by", label: "Reported by" },
    ],
  },
  maintenance: {
    title: "Maintenance Schedule",
    fetch: () => api.listMaint(),
    columns: [
      { k: "title", label: "Job" },
      { k: "type", label: "Type" },
      { k: "scheduled_date", label: "Scheduled", mono: true },
      { k: "derived_status", label: "Status", badge: true },
      { k: "where", label: "Asset", get: (r) => r.asset_name ? `${r.asset_code} ${r.asset_name}` : "—" },
    ],
  },
  pat: {
    title: "PAT / Electrical Test Register",
    fetch: () => api.listAssets(),
    columns: [
      { k: "asset_code", label: "Code", mono: true },
      { k: "name", label: "Asset" },
      { k: "location", label: "Location" },
      { k: "pat_status", label: "PAT status", badge: true },
      { k: "pat_last", label: "Last tested", mono: true },
      { k: "pat_result", label: "Result", badge: true },
      { k: "pat_next_due", label: "Next due", mono: true },
    ],
  },
};

export default function ListReport() {
  const { type } = useParams();
  const nav = useNavigate();
  const cfg = CONFIG[type];
  const [rows, setRows] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!cfg) { nav("/"); return; }
    cfg.fetch().then(setRows).catch(() => setRows([]));
  }, [type]); // eslint-disable-line

  const downloadPdf = async () => {
    const el = ref.current;
    if (!el) return;
    setPdfBusy(true);
    el.classList.add("pdf-mode");
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf().set({
        margin: [10, 10, 12, 10],
        filename: `${type}_report.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        pagebreak: { mode: ["css", "legacy"] },
      }).from(el).save();
      toast.success("PDF downloaded");
    } catch { toast.error("PDF export failed"); }
    finally { el.classList.remove("pdf-mode"); setPdfBusy(false); }
  };

  if (!cfg || rows === null) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="relative z-10 min-h-screen report-root">
      <div className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-border bg-background/90 px-4 py-3 backdrop-blur-xl no-print sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="gap-2"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.print()} className="gap-2 tap-scale"><Printer className="h-4 w-4" /> <span className="hidden sm:inline">Print</span></Button>
          <Button data-testid="list-report-pdf" onClick={downloadPdf} disabled={pdfBusy} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">
            {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
          </Button>
        </div>
      </div>

      <div ref={ref} className="mx-auto max-w-5xl px-5 py-8 sm:px-8">
        <div className="mb-5 flex items-end justify-between border-b-2 border-border pb-3">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary print-accent">Plantagenet Hall</span>
            <h1 className="mt-1 font-mono text-2xl font-extrabold tracking-tight text-foreground">{cfg.title}</h1>
          </div>
          <div className="text-right text-xs text-muted-foreground">{rows.length} records<br />{new Date().toLocaleDateString()}</div>
        </div>

        {rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No records.</p>
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                {cfg.columns.map((c) => <th key={c.k} className="py-2 pr-3">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border print-avoid-break">
                  {cfg.columns.map((c) => {
                    const val = c.get ? c.get(r) : r[c.k];
                    return (
                      <td key={c.k} className={`py-1.5 pr-3 ${c.mono ? "font-mono" : ""}`}>
                        {c.badge ? <StatusBadge value={val} /> : (val || "—")}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="mt-8 border-t border-border pt-3 text-center font-mono text-xs text-muted-foreground">Plantagenet Hall Tech Management · Generated {new Date().toLocaleString()}</div>
      </div>
    </div>
  );
}
